import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ParentNav } from '../../components/shared/BottomNav';
import {
  subscribeToCurrentWeek,
  subscribeToWeeks,
  subscribeToWeekTransactions,
  subscribeToActiveGoal,
  subscribeToGoalHistory,
  subscribeToRecurringTemplates,
  formatCurrency,
  getCurrentWeekId,
  getNextWeekId,
  getOrCreateCurrentWeek,
  getUnclosedPreviousWeek,
  weeksUntilTarget,
} from '../../services/firebase';

const CATEGORY_EMOJI = {
  food: '🍕', transport: '🚗', groceries: '🛒', entertainment: '🎬',
  clothing: '👕', health: '💊', books: '📚', other: '📦',
  gift: '🎁', job: '💼', refund: '↩️',
};

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const dayLabel = (dayIndex) => {
  const today = new Date().getDay();
  if (dayIndex === today) return 'Today';
  if (dayIndex === (today + 6) % 7) return 'Yesterday';
  return DAYS[dayIndex];
};

const ParentDashboard = () => {
  const { family, refreshKey } = useAuth();
  const navigate = useNavigate();
  const [currentWeek, setCurrentWeek] = useState(null);
  const [recentWeeks, setRecentWeeks] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [unclosedPrevWeek, setUnclosedPrevWeek] = useState(null);
  const [nextWeekTransactions, setNextWeekTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeGoal, setActiveGoal] = useState(null);
  const [goalHistory, setGoalHistory] = useState([]);
  const [recurringTemplates, setRecurringTemplates] = useState([]);

  const studentFirst = family?.studentName?.split(' ')[0] || 'your student';
  const allowance = family?.allowance?.amount || 0;

  useEffect(() => {
    if (!family?.id) return;
    getOrCreateCurrentWeek(family.id, allowance, family).catch(console.error);
    const unsubWeek = subscribeToCurrentWeek(family.id, (week) => {
      setCurrentWeek(week);
      setLoading(false);
    });
    const unsubWeeks = subscribeToWeeks(family.id, setRecentWeeks);
    return () => { unsubWeek(); unsubWeeks(); };
  }, [family?.id, allowance]);

  // Check for unclosed prior week — re-run when week rolls over or app foregrounds
  const currentWeekId = getCurrentWeekId();
  useEffect(() => {
    if (!family?.id) return;
    getUnclosedPreviousWeek(family.id)
      .then(week => setUnclosedPrevWeek(week))
      .catch(console.error);
  }, [family?.id, currentWeekId, refreshKey]);

  useEffect(() => {
    if (!family?.id) return;
    const weekId = getCurrentWeekId();
    const unsub = subscribeToWeekTransactions(family.id, weekId, setTransactions);
    return unsub;
  }, [family?.id]);

  useEffect(() => {
    if (!family?.id) return;
    const weekId = getNextWeekId();
    const unsub = subscribeToWeekTransactions(family.id, weekId, (txs) => {
      setNextWeekTransactions(txs.filter(t => t.type === 'planned'));
    });
    return unsub;
  }, [family?.id]);

  useEffect(() => {
    if (!family?.id) return;
    const unsub = subscribeToActiveGoal(family.id, setActiveGoal);
    return unsub;
  }, [family?.id]);

  useEffect(() => {
    if (!family?.id) return;
    const unsub = subscribeToGoalHistory(family.id, setGoalHistory);
    return unsub;
  }, [family?.id]);

  useEffect(() => {
    if (!family?.id) return;
    const unsub = subscribeToRecurringTemplates(family.id, setRecurringTemplates);
    return unsub;
  }, [family?.id]);

  if (!family?.studentId) {
    return (
      <div className="screen">
        <ParentNav />
        <div style={{ textAlign: 'center', paddingTop: 60 }}>
          <div style={{ fontSize: 56, marginBottom: 20 }}>⏳</div>
          <h2>Waiting for {studentFirst}</h2>
          <p style={{ color: 'var(--color-text-2)', marginTop: 12, lineHeight: 1.6 }}>
            Share the invite link with {studentFirst} so they can connect their account.
          </p>
          <button className="btn btn--primary" style={{ marginTop: 32 }}
            onClick={() => navigate('/settings')}>
            View Invite Link
          </button>
        </div>
      </div>
    );
  }

  // Derived values — mirrors student dashboard logic
  const spent = currentWeek?.spent || 0;
  const planned = currentWeek?.plannedSpend || 0;
  const bonusIncome = currentWeek?.bonusIncome || 0;
  const saved = currentWeek?.saved || 0;
  const matchEarned = currentWeek?.matchEarned || 0;
  const totalAvailable = (currentWeek?.allowanceAmount || allowance) + bonusIncome;
  const projectedSpend = spent + planned;
  const remaining = Math.max(0, totalAvailable - spent - planned - saved);
  const spentPct = totalAvailable > 0 ? Math.min(100, (spent / totalAvailable) * 100) : 0;
  const plannedPct = totalAvailable > 0 ? Math.min(100, (planned / totalAvailable) * 100) : 0;
  const savedPct = totalAvailable > 0 ? Math.min(100, (saved / totalAvailable) * 100) : 0;
  const remainPct = Math.max(0, 100 - spentPct - plannedPct - savedPct);
  const isClosed = currentWeek?.closedOut;
  const carryover = currentWeek?.carryoverFromPrev || 0;
  const isOverBudget = projectedSpend + saved > totalAvailable;
  const nearBudget = !isOverBudget && totalAvailable > 0 && (projectedSpend + saved) / totalAvailable > 0.8;

  // Split transactions by type
  const actualTxs = transactions.filter(t => (t.type === 'actual' || !t.type) && t.type !== 'deleted' && t.type !== 'converted');
  const plannedTxs = transactions.filter(t => t.type === 'planned');
  const incomeTxs = transactions.filter(t => t.type === 'income');

  // Lifetime stats
  const totalSaved = recentWeeks.reduce((sum, w) => sum + (w.saved || 0), 0);
  const totalMatch = recentWeeks.reduce((sum, w) => sum + (w.matchEarned || 0), 0);

  // Waiting for Monday — show placeholder
  if (family?.startOnSunday && !family?.walletStarted) {
    const now = new Date();
    const daysUntilMonday = now.getDay() === 1 ? 7 : (8 - now.getDay()) % 7 || 7;
    const nextMonday = new Date(now.getFullYear(), now.getMonth(), now.getDate() + daysUntilMonday);
    const daysLeft = Math.ceil((nextMonday - now) / 86400000);
    const dateStr = nextMonday.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return (
      <div className="screen">
        <ParentNav />
        <div className="page-header">
          <div>
            <p style={{ fontSize: '0.875rem', color: 'var(--color-text-3)', marginBottom: 2 }}>Watching over</p>
            <h1 className="page-title">{studentFirst}'s Wallet</h1>
          </div>
          <div style={avatarStyle}>{studentFirst[0].toUpperCase()}</div>
        </div>
        <div className="card--hero animate-in" style={{ textAlign: 'center', padding: '32px 20px', marginBottom: 16 }}>
          <div style={{ width: 72, height: 72, borderRadius: '50%',
            background: 'rgba(255,255,255,0.15)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
              <circle cx="18" cy="18" r="14" stroke="white" strokeWidth="2" strokeOpacity="0.6"/>
              <line x1="18" y1="10" x2="18" y2="18" stroke="white" strokeWidth="2" strokeLinecap="round"/>
              <line x1="18" y1="18" x2="24" y2="22" stroke="white" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </div>
          <div style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: 8 }}>
            First week starts Monday
          </div>
          <div style={{ fontSize: '0.875rem', opacity: 0.8, marginBottom: 10, lineHeight: 1.5 }}>
            {studentFirst}'s {formatCurrency(allowance)} allowance will be ready on {dateStr} by 9am ET.
          </div>
          <div style={{ fontSize: '0.75rem', opacity: 0.6 }}>
            {daysLeft === 1 ? '1 day to go' : `${daysLeft} days to go`}
          </div>
        </div>
        <div className="card" style={{ padding: '16px 18px' }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <span style={{ fontSize: 18, flexShrink: 0 }}>
              {family?.inviteAccepted ? '✅' : '📬'}
            </span>
            <div>
              <div style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--color-primary)', marginBottom: 4 }}>
                {family?.inviteAccepted
                  ? `${studentFirst}'s account is connected`
                  : `Invite sent to ${studentFirst}`}
              </div>
              <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-2)', lineHeight: 1.6 }}>
                {family?.inviteAccepted
                  ? `${studentFirst} is ready to go — wallet activates Monday ${dateStr} by 9am ET.`
                  : `Once they accept, they'll see a countdown until Monday when their wallet activates.`}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="screen">
      <ParentNav />

      {/* Header */}
      <div className="page-header">
        <div>
          <p style={{ fontSize: '0.875rem', color: 'var(--color-text-3)', marginBottom: 2 }}>
            Watching over
          </p>
          <h1 className="page-title">{studentFirst}'s Wallet</h1>
        </div>
        <div style={avatarStyle}>{studentFirst[0].toUpperCase()}</div>
      </div>

      {/* Hero card */}
      <div className="card--hero animate-in" style={{ marginBottom: 20 }}>

        {/* Budget header + badge */}
        <div style={{ display: 'flex', justifyContent: 'space-between',
          alignItems: 'flex-start', marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: '0.6875rem', opacity: 0.6, fontWeight: 600,
              letterSpacing: '0.05em', marginBottom: 6 }}>
              {isClosed ? 'WEEK\'S BUDGET' : 'THIS WEEK\'S BUDGET'}
            </div>
            <div style={{ fontSize: '1.75rem', fontWeight: 700,
              letterSpacing: '-0.5px', lineHeight: 1 }}>
              {formatCurrency(totalAvailable)}
            </div>
            {(carryover !== 0 || bonusIncome > 0) && (
              <div style={{ fontSize: '0.6875rem', opacity: 0.6, marginTop: 4 }}>
                {formatCurrency(currentWeek?.allowanceAmount - carryover || allowance)} allowance
                {carryover !== 0 && ` · ${carryover > 0 ? '+' : ''}${formatCurrency(carryover)} carryover`}
                {bonusIncome > 0 && ` · +${formatCurrency(bonusIncome)} income`}
              </div>
            )}
          </div>
          {isClosed
            ? <span className="badge" style={{ background: 'rgba(82,183,136,0.35)',
                color: '#A8EDCA', flexShrink: 0 }}>Closed ✓</span>
            : <span className="badge" style={{ background: 'rgba(255,255,255,0.2)',
                color: 'white', flexShrink: 0 }}>Active</span>
          }
        </div>

        <div style={{ height: 1, background: 'rgba(255,255,255,0.15)', marginBottom: 14 }} />

        {/* Tiles — open week: Spent / Planned / Projected; closed week: Spent / Saved / Rolled fwd */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          <div style={{ flex: 1, background: 'rgba(255,255,255,0.1)',
            borderRadius: 8, padding: '10px 12px' }}>
            <div style={{ fontSize: '0.6875rem', opacity: 0.7, marginBottom: 3 }}>Spent</div>
            <div style={{ fontSize: '1.0625rem', fontWeight: 700 }}>{formatCurrency(spent)}</div>
          </div>
          {isClosed ? (
            <>
              <div style={{ flex: 1, background: 'rgba(82,183,136,0.25)',
                borderRadius: 8, padding: '10px 12px' }}>
                <div style={{ fontSize: '0.6875rem', opacity: 0.7, marginBottom: 3 }}>Saved</div>
                <div style={{ fontSize: '1.0625rem', fontWeight: 700,
                  color: '#A8EDCA' }}>{formatCurrency(saved)}</div>
              </div>
              <div style={{ flex: 1, background: 'rgba(255,255,255,0.1)',
                borderRadius: 8, padding: '10px 12px' }}>
                <div style={{ fontSize: '0.6875rem', opacity: 0.7, marginBottom: 3 }}>Rolled fwd</div>
                <div style={{ fontSize: '1.0625rem', fontWeight: 700 }}>
                  {formatCurrency(Math.max(0, remaining))}
                </div>
              </div>
            </>
          ) : (
            <>
              {planned > 0 && (
                <div style={{ flex: 1, background: 'rgba(255,255,255,0.1)',
                  borderRadius: 8, padding: '10px 12px' }}>
                  <div style={{ fontSize: '0.6875rem', opacity: 0.7, marginBottom: 3 }}>Planned</div>
                  <div style={{ fontSize: '1.0625rem', fontWeight: 700 }}>{formatCurrency(planned)}</div>
                </div>
              )}
              {planned > 0 && (
                <div style={{ flex: 1, borderRadius: 8, padding: '10px 12px',
                  background: isOverBudget
                    ? 'rgba(220,50,50,0.3)'
                    : nearBudget
                      ? 'rgba(244,162,97,0.3)'
                      : 'rgba(255,255,255,0.15)',
                  border: isOverBudget
                    ? '1px solid rgba(255,120,120,0.4)'
                    : nearBudget
                      ? '1px solid rgba(244,162,97,0.4)'
                      : 'none',
                }}>
                  <div style={{ fontSize: '0.6875rem', opacity: 0.7, marginBottom: 3 }}>Projected</div>
                  <div style={{ fontSize: '1.0625rem', fontWeight: 700,
                    color: isOverBudget ? '#FFDDD2' : nearBudget ? '#FFE8CC' : 'white' }}>
                    {formatCurrency(projectedSpend)}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Bar */}
        <div style={{ height: 10, background: 'rgba(255,255,255,0.2)',
          borderRadius: 'var(--radius-full)', overflow: 'hidden', display: 'flex', marginBottom: 8 }}>
          <div style={{ width: `${spentPct}%`, background: '#F4A261', transition: 'width 0.5s ease' }} />
          <div style={{ width: `${plannedPct}%`,
            background: 'repeating-linear-gradient(45deg,#F4A261,#F4A261 4px,#FFDDD2 4px,#FFDDD2 8px)',
            transition: 'width 0.5s ease' }} />
          <div style={{ width: `${savedPct}%`, background: '#52B788', transition: 'width 0.5s ease' }} />
          <div style={{ width: `${remainPct}%`, background: 'rgba(255,255,255,0.15)' }} />
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px',
          fontSize: '0.75rem', opacity: 0.85, marginBottom: 12 }}>
          <LegendItem color="#F4A261" label="Spent" value={formatCurrency(spent)} />
          {planned > 0 && <LegendItem color="#FFDDD2" label="Planned" value={formatCurrency(planned)} striped />}
          {saved > 0 && <LegendItem color="#52B788" label="Saved" value={formatCurrency(saved)} />}
          <LegendItem color="rgba(255,255,255,0.3)" label="Left" value={formatCurrency(remaining)} />
        </div>

        {/* Left to spend — summary line at bottom */}
        {!isClosed && (
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.15)', paddingTop: 12,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '0.8125rem', opacity: 0.8 }}>Left to spend</span>
            <span style={{ fontSize: '1.25rem', fontWeight: 700,
              color: isOverBudget ? '#FFDDD2' : 'white' }}>
              {formatCurrency(remaining)}
            </span>
          </div>
        )}

        {/* Match earned */}
        {family?.match?.enabled && matchEarned > 0 && (
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: 12,
            marginTop: isClosed ? 0 : 12,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ opacity: 0.8, fontSize: '0.875rem' }}>🎯 Match earned this week</span>
            <span style={{ fontWeight: 700, fontSize: '1.1rem' }}>{formatCurrency(matchEarned)}</span>
          </div>
        )}
      </div>

      {/* Smart nudge card */}
      <NudgeCard
        studentFirst={studentFirst}
        spent={spent}
        planned={planned}
        remaining={remaining}
        totalAvailable={totalAvailable}
        isClosed={isClosed}
        isOverBudget={isOverBudget}
        actualTxs={actualTxs}
        plannedTxs={plannedTxs}
        recentWeeks={recentWeeks}
        matchEnabled={family?.match?.enabled}
        matchEarned={matchEarned}
        totalSaved={totalSaved}
        totalMatch={totalMatch}
        unclosedPrevWeek={unclosedPrevWeek}
        activeGoal={activeGoal}
        goalHistory={goalHistory}
      />

      {/* Savings goal card — read-only parent view */}
      {activeGoal && (
        <ParentGoalCard goal={activeGoal} studentFirst={studentFirst} />
      )}

      {/* Completed goal notification */}
      {goalHistory.filter(g => g.status === 'completed').map((g, i) => (
        <CompletedGoalNotice key={g.name + i} goal={g} studentFirst={studentFirst} />
      ))}

      {/* Transaction sections */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
          <div className="spinner" />
        </div>
      ) : (
        <div className="stack stack-5">

          {/* Planned */}
          {plannedTxs.length > 0 && (
            <ReadOnlyTxSection
              title="Planned this week"
              emoji="📅"
              transactions={plannedTxs}
              color="var(--color-accent)"
              type="planned"
              studentFirst={studentFirst}
            />
          )}

          {/* Actual spending */}
          <ReadOnlyTxSection
            title="Spent this week"
            emoji="💸"
            transactions={actualTxs}
            color="var(--color-error)"
            type="actual"
            allTransactions={transactions}
            studentFirst={studentFirst}
            emptyMessage={`${studentFirst} hasn't logged any spending yet this week`}
          />

          {/* Income */}
          {incomeTxs.length > 0 && (
            <ReadOnlyTxSection
              title="Income this week"
              emoji="🎁"
              transactions={incomeTxs}
              color="var(--color-primary)"
              type="income"
              studentFirst={studentFirst}
            />
          )}

          {/* Next week planned preview */}
          {nextWeekTransactions.length > 0 && (
            <ParentNextWeekPreview
              transactions={nextWeekTransactions}
              studentFirst={studentFirst}
              recurringTemplates={recurringTemplates}
            />
          )}
        </div>
      )}

      {/* Match reminder */}
      {family?.match?.enabled && (
        <div className="card--accent animate-in" style={{ marginTop: 24 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <span style={{ fontSize: 20 }}>🎯</span>
            <div>
              <div style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--color-primary)' }}>
                Match active
              </div>
              <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-2)', marginTop: 2 }}>
                {family.match.type === 'percentage'
                  ? `You're matching ${family.match.value}% of ${studentFirst}'s savings`
                  : `You're adding $${family.match.value} per dollar ${studentFirst} saves`}
                {family.match.monthlyCap > 0 && `, up to $${family.match.monthlyCap}/month`}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Read-only transaction section (no edit/delete actions) ──────────────────

const ReadOnlyTxSection = ({ title, emoji, transactions, color, type, studentFirst, emptyMessage, allTransactions }) => {
  if (transactions.length === 0) {
    if (!emptyMessage) return null;
    return (
      <div>
        <h3 style={{ marginBottom: 12, fontSize: '1rem' }}>{emoji} {title}</h3>
        <div className="card" style={{ textAlign: 'center', padding: 28 }}>
          <p style={{ color: 'var(--color-text-3)', fontSize: '0.9rem' }}>{emptyMessage}</p>
        </div>
      </div>
    );
  }

  const today = new Date().getDay();

  // Get display day — prefer spendDate, fall back to createdAt timestamp
  const getTxDay = (tx) => {
    if (tx.spendDate !== undefined) return tx.spendDate;
    if (tx.createdAt?.toDate) return tx.createdAt.toDate().getDay();
    if (tx.createdAt?.seconds) return new Date(tx.createdAt.seconds * 1000).getDay();
    return today;
  };

  const getTxTime = (tx) => {
    if (tx.createdAt?.toDate) return tx.createdAt.toDate().getTime();
    if (tx.createdAt?.seconds) return tx.createdAt.seconds * 1000;
    return 0;
  };

  const getDayLabel = (dayIndex, asHeader = false) => {
    if (dayIndex === today) {
      return (type === 'actual' && asHeader) ? 'As of Today' : 'Today';
    }
    if (dayIndex === (today + 6) % 7) return 'Yesterday';
    return DAYS[dayIndex];
  };

  const grouped = transactions.reduce((acc, tx) => {
    const day = getTxDay(tx);
    if (!acc[day]) acc[day] = [];
    acc[day].push(tx);
    return acc;
  }, {});

  const sortedDays = Object.keys(grouped)
    .map(Number)
    .sort((a, b) => {
      if (type === 'planned') return a - b; // upcoming days first
      // actual/income: sort by most recent transaction in group
      const latestA = Math.max(...grouped[a].map(getTxTime));
      const latestB = Math.max(...grouped[b].map(getTxTime));
      return latestB - latestA;
    });

  const total = transactions.reduce((s, t) => s + t.amount, 0);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ fontSize: '1rem' }}>{emoji} {title}</h3>
        <span style={{ fontWeight: 700, color, fontSize: '0.9375rem' }}>
          {type === 'income' ? '+' : '-'}{formatCurrency(total)}
        </span>
      </div>

      <div className="stack stack-4">
        {sortedDays.map(day => (
          <div key={day}>
            {/* Day header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: '0.75rem', fontWeight: 700,
                color: 'var(--color-text-3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                {getDayLabel(day, true)}
              </span>
              <div style={{ flex: 1, height: 1, background: 'var(--color-border)' }} />
            </div>

            <div className="stack stack-2">
              {grouped[day].map(tx => {
                const txDay = getTxDay(tx);
                const dayStr = getDayLabel(txDay);
                // Find original planned amount if converted
                const originalPlanned = tx.convertedFromId
                  ? (allTransactions || []).find(t => t.id === tx.convertedFromId)
                  : null;
                return (
                <div key={tx.id} className="card"
                  style={{ padding: '12px 14px', display: 'flex', alignItems: 'center',
                    gap: 12, borderLeft: `3px solid ${color}` }}>
                  <div style={{ width: 38, height: 38, borderRadius: 10,
                    background: 'var(--color-surface-2)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>
                    {CATEGORY_EMOJI[tx.category] || '📦'}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.9rem',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {tx.note || tx.category}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--color-text-3)',
                      marginTop: 1, display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={{ textTransform: 'capitalize' }}>{tx.category}</span>
                      {type === 'planned' && (
                        <span style={{ color: 'var(--color-accent)', fontWeight: 600 }}>· planned</span>
                      )}
                      {originalPlanned && originalPlanned.amount !== tx.amount && (
                        <span style={{ color: 'var(--color-text-3)' }}>
                          · planned {formatCurrency(originalPlanned.amount)}
                          {tx.amount > originalPlanned.amount
                            ? <span style={{ color: 'var(--color-error)' }}> ↑</span>
                            : <span style={{ color: 'var(--color-primary)' }}> ↓</span>
                          }
                        </span>
                      )}
                      <span>· {dayStr}</span>
                    </div>
                  </div>
                  <div style={{ fontWeight: 700, color, flexShrink: 0 }}>
                    {type === 'income' ? '+' : '-'}{formatCurrency(tx.amount)}
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Parent next-week preview (read-only) ────────────────────────────────────

const DAYS_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const ParentNextWeekPreview = ({ transactions, studentFirst, recurringTemplates = [] }) => {
  const [open, setOpen] = useState(false);
  const total = transactions.reduce((s, t) => s + t.amount, 0);

  return (
    <>
      {/* "Next week" divider */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <div style={{ flex: 1, height: 1, background: 'var(--color-border)' }} />
        <span style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--color-text-3)',
          letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>
          next week
        </span>
        <div style={{ flex: 1, height: 1, background: 'var(--color-border)' }} />
      </div>
      <div className="card" style={{ padding: 0, overflow: 'hidden',
        background: 'var(--color-surface-2)' }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 16px', cursor: 'pointer', userSelect: 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 16 }}>📋</span>
          <span style={{ fontWeight: 600, fontSize: '0.9375rem' }}>Next week</span>
          <span style={{
            fontSize: '0.7rem', fontWeight: 700,
            background: 'var(--color-primary-pale)', color: 'var(--color-primary)',
            padding: '2px 8px', borderRadius: 'var(--radius-full)',
          }}>
            {transactions.length} planned
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: '0.875rem', color: 'var(--color-text-2)' }}>
            -{formatCurrency(total)}
          </span>
          <span style={{
            fontSize: '0.7rem', color: 'var(--color-text-3)',
            display: 'inline-block', transition: 'transform 200ms',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          }}>▼</span>
        </div>
      </div>

      {open && (
        <div style={{ borderTop: '1px solid var(--color-border)', padding: '10px 16px 14px' }}>
          <div className="stack stack-2">
            {transactions.map(tx => {
              const dayName = tx.spendDate !== undefined ? DAYS_FULL[tx.spendDate] : 'Next week';
              return (
                <div key={tx.id} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 0', borderBottom: '1px solid var(--color-border)',
                }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: 8,
                    background: 'var(--color-surface-2)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 15, flexShrink: 0,
                  }}>
                    {CATEGORY_EMOJI[tx.category] || '📦'}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.875rem',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {tx.note || tx.category}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--color-text-3)',
                      marginTop: 1, display: 'flex', alignItems: 'center', gap: 4 }}>
                      {dayName}
                      {recurringTemplates.some(t =>
                        t.category === tx.category &&
                        t.spendDay === tx.spendDate &&
                        Math.abs(t.amount - tx.amount) < 0.01
                      ) && (
                        <span style={{ fontSize: 10, opacity: 0.7 }} title="Recurring">🔁</span>
                      )}
                    </div>
                  </div>
                  <span style={{ fontSize: '0.875rem', fontWeight: 600,
                    color: 'var(--color-accent)', flexShrink: 0 }}>
                    -{formatCurrency(tx.amount)}
                  </span>
                </div>
              );
            })}
          </div>
          <p style={{
            fontSize: '0.75rem', color: 'var(--color-text-3)',
            marginTop: 12, lineHeight: 1.5,
          }}>
            {studentFirst} is planning ahead for next week.
          </p>
        </div>
      )}
    </div>
    </>
  );
};


// ─── Parent goal card (read-only) ────────────────────────────────────────────

const ParentGoalCard = ({ goal, studentFirst }) => {
  const pct = Math.min(100, Math.round((goal.amountSaved / goal.targetAmount) * 100));
  const remaining = Math.max(0, Math.round((goal.targetAmount - goal.amountSaved) * 100) / 100);

  const targetDate = goal.targetDate?.toDate
    ? goal.targetDate.toDate()
    : goal.targetDate?.seconds
      ? new Date(goal.targetDate.seconds * 1000)
      : null;

  const weeksLeft = weeksUntilTarget(targetDate);

  const dateStr = targetDate
    ? targetDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : '—';

  const effectiveWeeklyTarget = weeksLeft && weeksLeft > 0 && remaining > 0
    ? Math.round(Math.ceil((remaining / weeksLeft) * 100) / 100 * 100) / 100
    : goal.weeklyTarget;

  return (
    <div className="card" style={{ marginBottom: 20, padding: 0, overflow: 'hidden' }}>
      <div style={{ padding: '14px 16px 12px', display: 'flex',
        justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12,
            background: 'var(--color-primary-pale)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>
            ⭐
          </div>
          <div>
            <div style={{ fontSize: '0.6875rem', fontWeight: 600,
              color: 'var(--color-text-3)', textTransform: 'uppercase',
              letterSpacing: '0.05em', marginBottom: 2 }}>
              {studentFirst}'s savings goal
            </div>
            <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--color-text)' }}>
              {goal.name}
            </div>
          </div>
        </div>
        <span className="badge badge--green">{pct}%</span>
      </div>

      <div style={{ padding: '0 16px 14px' }}>
        <div style={{ marginBottom: 8 }}>
          <span style={{ fontFamily: 'var(--font-display)', fontSize: '1.375rem',
            fontWeight: 700, color: 'var(--color-primary)' }}>
            {formatCurrency(goal.amountSaved)}
          </span>
          <span style={{ fontSize: '0.875rem', color: 'var(--color-text-3)', marginLeft: 6 }}>
            reserved for {formatCurrency(goal.targetAmount)} goal
          </span>
        </div>
        <div className="progress-bar" style={{ marginBottom: 6 }}>
          <div className="progress-bar__fill" style={{ width: `${pct}%` }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ fontSize: '0.75rem', color: 'var(--color-text-3)' }}>
            {formatCurrency(remaining)} to go
          </span>
          <span style={{ fontSize: '0.75rem', color: 'var(--color-primary)', fontWeight: 600 }}>
            Target: {dateStr}
          </span>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 8, padding: '0 16px 14px' }}>
        {[
          { label: 'Target date', value: dateStr },
          { label: 'Weeks left', value: weeksLeft ?? '—' },
          { label: 'Save/week', value: formatCurrency(effectiveWeeklyTarget) },
        ].map(({ label, value }) => (
          <div key={label} style={{ background: 'var(--color-surface-2)',
            borderRadius: 'var(--radius-sm)', padding: '8px 10px', textAlign: 'center' }}>
            <div style={{ fontWeight: 700, fontSize: '0.875rem', color: 'var(--color-text)' }}>
              {value}
            </div>
            <div style={{ fontSize: '0.6875rem', color: 'var(--color-text-3)', marginTop: 2 }}>
              {label}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Completed goal notice (read-only, collapsed by default) ─────────────────

const CompletedGoalNotice = ({ goal, studentFirst }) => {
  const [open, setOpen] = useState(false);

  // Only show goals completed in the last 30 days
  const completedDate = goal.completedAt?.toDate
    ? goal.completedAt.toDate()
    : goal.completedAt?.seconds
      ? new Date(goal.completedAt.seconds * 1000)
      : null;

  if (completedDate) {
    const daysSince = (new Date() - completedDate) / (1000 * 60 * 60 * 24);
    if (daysSince > 30) return null;
  }

  const dateStr = completedDate
    ? completedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : '';

  const isPurchased = goal.purchasedAt || goal.spentFromSavings;

  return (
    <div style={{
      background: 'var(--color-primary-pale)',
      border: '1.5px solid var(--color-primary-light)',
      borderRadius: 'var(--radius-lg)',
      marginBottom: 16,
      overflow: 'hidden',
    }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 16px', cursor: 'pointer' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 18 }}>🎉</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--color-primary)' }}>
              {studentFirst} hit their {goal.name} goal!
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--color-primary)', opacity: 0.8 }}>
              {formatCurrency(goal.targetAmount)} saved{dateStr ? ` · ${dateStr}` : ''}
              {isPurchased ? ' · purchased ✓' : ' · awaiting purchase'}
            </div>
          </div>
        </div>
        <span style={{ fontSize: '0.7rem', color: 'var(--color-primary)',
          transform: open ? 'rotate(180deg)' : 'none', display: 'inline-block',
          transition: 'transform 200ms' }}>▼</span>
      </div>
      {open && (
        <div style={{ padding: '0 16px 14px', borderTop: '1px solid var(--color-primary-light)' }}>
          <p style={{ fontSize: '0.8125rem', color: 'var(--color-primary)',
            lineHeight: 1.6, marginTop: 10 }}>
            {isPurchased
              ? `${studentFirst} confirmed they purchased their ${goal.name}. ${formatCurrency(goal.targetAmount)} has been deducted from their savings balance.`
              : `${studentFirst} reached their savings goal but hasn't confirmed the purchase yet. The amount is still in their savings balance until they do.`}
          </p>
        </div>
      )}
    </div>
  );
};

// ─── Sub-components ──────────────────────────────────────────────────────────

const LegendItem = ({ color, label, value, striped }) => (
  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
    <span style={{
      width: 10, height: 10, borderRadius: 2, flexShrink: 0, display: 'inline-block',
      background: striped
        ? 'repeating-linear-gradient(45deg,#F4A261,#F4A261 3px,#FFDDD2 3px,#FFDDD2 6px)'
        : color,
    }} />
    {label} {value}
  </span>
);

// ─── NudgeCard ────────────────────────────────────────────────────────────────
// Picks the most contextually relevant message based on current state

const NudgeCard = ({
  studentFirst, spent, planned, remaining, totalAvailable,
  isClosed, isOverBudget, actualTxs, plannedTxs, recentWeeks,
  matchEnabled, matchEarned, totalSaved, totalMatch, unclosedPrevWeek,
  activeGoal, goalHistory,
}) => {
  const closedWeeks = recentWeeks.filter(w => w.closedOut);
  const lastClosedWeek = closedWeeks[0];
  const weeksTracked = recentWeeks.length;
  const pctSpent = totalAvailable > 0 ? spent / totalAvailable : 0;
  const today = new Date().getDay(); // 0=Sun, 6=Sat
  const daysLeft = today === 0 ? 0 : 7 - today; // days until Sunday (end of Mon-Sun week)

  // Pick the most relevant nudge — ordered by priority
  const nudge = (() => {

    // 1. Unclosed prior week — most urgent
    if (unclosedPrevWeek) {
      const prevSpent = unclosedPrevWeek.spent || 0;
      const prevPlanned = unclosedPrevWeek.plannedSpend || 0;
      const prevAllowance = unclosedPrevWeek.allowanceAmount || 0;
      const prevIsOver = (prevSpent + prevPlanned) > prevAllowance;
      const overage = Math.abs(prevAllowance - (prevSpent + prevPlanned));
      return {
        emoji: '⚠️',
        bg: '#FFF3E0', border: '#F4A261', color: '#C45E1A',
        title: `${studentFirst} hasn't closed out last week`,
        body: prevIsOver
          ? `They went over budget last week — the overage carries into this week once they close out. Worth a nudge.`
          : `Savings and match won't be locked in until they do. Worth a nudge.`,
      };
    }

    // 1b. Goal just completed — high priority celebration
    const justCompletedGoal = goalHistory?.find(
      g => g.status === 'completed' && !g.purchasedAt
    );
    if (justCompletedGoal) return {
      emoji: '⭐',
      bg: 'var(--color-primary-pale)', border: 'var(--color-primary-light)', color: 'var(--color-primary)',
      title: `${studentFirst} hit their ${justCompletedGoal.name} goal!`,
      body: `${formatCurrency(justCompletedGoal.targetAmount)} saved. They'll confirm the purchase on their Savings tab.`,
    };

    // 1c. Goal behind pace
    if (activeGoal) {
      const targetDate = activeGoal.targetDate?.toDate
        ? activeGoal.targetDate.toDate()
        : activeGoal.targetDate?.seconds
          ? new Date(activeGoal.targetDate.seconds * 1000)
          : null;
      const weeksLeft = weeksUntilTarget(targetDate);
      const pctComplete = activeGoal.targetAmount > 0
        ? activeGoal.amountSaved / activeGoal.targetAmount
        : 0;
      const expectedPct = weeksLeft !== null && activeGoal.weeklyTarget > 0
        ? 1 - (weeksLeft * activeGoal.weeklyTarget / activeGoal.targetAmount)
        : null;
      const isBehindPace = expectedPct !== null && pctComplete < expectedPct - 0.05;

      if (isBehindPace && weeksLeft !== null && weeksLeft <= 4) return {
        emoji: '⏳',
        bg: '#FFF3E0', border: '#F4A261', color: '#C45E1A',
        title: `${studentFirst}'s ${activeGoal.name} goal is behind pace`,
        body: `The target date is ${weeksLeft === 1 ? 'next week' : `${weeksLeft} weeks away`} and they're not quite on track. A gentle reminder at close-out could help.`,
      };
    }

    // 2. Week just closed — celebratory
    if (isClosed && lastClosedWeek) {
      const weekSaved = lastClosedWeek.saved || 0;
      const weekMatch = lastClosedWeek.matchEarned || 0;
      if (weekSaved > 0) return {
        emoji: '🎉',
        bg: 'var(--color-primary-pale)', border: 'var(--color-primary-light)', color: 'var(--color-primary)',
        title: `${studentFirst} closed out this week`,
        body: matchEnabled && weekMatch > 0
          ? `They saved ${formatCurrency(weekSaved)} and earned a ${formatCurrency(weekMatch)} match. Total to bank: ${formatCurrency(weekSaved + weekMatch)}.`
          : `They saved ${formatCurrency(weekSaved)} this week. Nice work.`,
      };
      return {
        emoji: '✅',
        bg: 'var(--color-primary-pale)', border: 'var(--color-primary-light)', color: 'var(--color-primary)',
        title: 'Week closed out',
        body: `${studentFirst} closed the week without saving — that happens. New week starts fresh.`,
      };
    }

    // 3. Nothing logged yet this week — day-aware messaging
    if (actualTxs.length === 0 && plannedTxs.length === 0) {
      const dayOfWeek = new Date().getDay(); // 0=Sun, 1=Mon...
      const isMidWeek = dayOfWeek >= 3 && dayOfWeek <= 5; // Wed, Thu, Fri
      const dayName = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'][dayOfWeek];
      return {
        emoji: '👀',
        bg: 'var(--color-surface-2)', border: 'var(--color-border)', color: 'var(--color-text-2)',
        title: `${studentFirst} hasn't logged anything yet`,
        body: isMidWeek
          ? `It's ${dayName} and nothing has been logged yet. Could be a quiet week — or they may have forgotten. Worth a check-in.`
          : weeksTracked > 1
            ? `Last week they spent ${formatCurrency(lastClosedWeek?.spent || 0)}. This week is a blank slate.`
            : `Once they do, you'll see their planned spend, actual spend, and income here in real time.`,
      };
    }

    // 3b. Overdue planned items — fires regardless of whether actuals exist
    if (plannedTxs.length > 0 && !isClosed) {
      const todayDay = new Date().getDay();
      const overdueCount = plannedTxs.filter(t => {
        if (t.spendDate === undefined) return false;
        const itemPos = t.spendDate === 0 ? 7 : t.spendDate;
        const todayPos = todayDay === 0 ? 7 : todayDay;
        return itemPos < todayPos;
      }).length;

      if (overdueCount > 0) return {
        emoji: '💡',
        bg: '#FFF3E0', border: '#F4A261', color: '#C45E1A',
        title: `Check in with ${studentFirst}`,
        body: `${overdueCount} planned ${overdueCount === 1 ? 'item was' : 'items were'} scheduled earlier this week but ${overdueCount === 1 ? "hasn't" : "haven't"} been logged yet. Worth a check-in.`,
      };
    }

    // 3c. Planned before spending — student has planned but not yet spent anything
    if (plannedTxs.length > 0 && actualTxs.length === 0) return {
      emoji: '💡',
      bg: 'var(--color-primary-pale)', border: 'var(--color-primary-light)', color: 'var(--color-primary)',
      title: `${studentFirst} is planning before spending. Smart.`,
      body: `Having a plan is half the battle — now it's about sticking to it.`,
    };

    // 4. Over budget warning
    if (isOverBudget) return {
      emoji: '⚠️',
      bg: '#FFF0F0', border: '#FFDDD2', color: 'var(--color-error)',
      title: `${studentFirst} is over budget`,
      body: `Whatever they go over will be deducted from next week's budget automatically — no action needed on your end, but worth a heads up to them.`,
    };

    // 5. Closing in on budget (>80% spent)
    if (pctSpent > 0.80 && !isClosed && daysLeft > 1) return {
      emoji: '📉',
      bg: '#FFF3E0', border: '#F4A261', color: '#C45E1A',
      title: `${studentFirst} is close to the limit`,
      body: `${daysLeft > 2 ? `Still ${daysLeft} days left in the week.` : 'Just a day or two left.'} This is where the planning habit either holds or doesn't.`,
    };

    // 6. Has planned items — planning is happening
    if (plannedTxs.length > 0 && actualTxs.length > 0) return {
      emoji: '📋',
      bg: 'var(--color-primary-pale)', border: 'var(--color-primary-light)', color: 'var(--color-primary)',
      title: `${studentFirst} is planning ahead`,
      body: `They're tracking both planned and actual spend. Nothing to do — just watch how it unfolds.`,
    };

    // 7. Match milestone — total savings achievement
    if (matchEnabled && totalSaved >= 100) return {
      emoji: '🏆',
      bg: 'var(--color-primary-pale)', border: 'var(--color-primary-light)', color: 'var(--color-primary)',
      title: `${studentFirst} has saved ${formatCurrency(totalSaved)} total`,
      body: `Your match has added ${formatCurrency(totalMatch)} on top of that — real money that wouldn't exist without good money management.`,
    };

    // 8. Multi-week streak under budget
    if (closedWeeks.length >= 3) {
      const underBudget = closedWeeks.slice(0, 3).every(w =>
        (w.spent || 0) <= (w.allowanceAmount || totalAvailable)
      );
      if (underBudget) return {
        emoji: '🔥',
        bg: 'var(--color-primary-pale)', border: 'var(--color-primary-light)', color: 'var(--color-primary)',
        title: `3 weeks under budget in a row`,
        body: `${studentFirst} is on a roll${matchEnabled ? ' and earning the rewards.' : ' — three weeks of discipline in a row.'}`,
      };
    }

    // 9. Good pace — spending normally, week in progress
    if (actualTxs.length > 0 && pctSpent < 0.6) return {
      emoji: '👍',
      bg: 'var(--color-primary-pale)', border: 'var(--color-primary-light)', color: 'var(--color-primary)',
      title: `${studentFirst} is on track`,
      body: `Spending is paced well with ${daysLeft > 0 ? `${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} still to go` : 'the week wrapping up today'}. Plenty of room to save something at close-out.`,
    };

    // 10. Default — just getting started
    return {
      emoji: '👋',
      bg: 'var(--color-surface-2)', border: 'var(--color-border)', color: 'var(--color-text-2)',
      title: `You're all set`,
      body: `${studentFirst}'s wallet is connected. You'll see spending and savings here as the week unfolds.`,
    };
  })();

  return (
    <div style={{
      background: nudge.bg,
      border: `1.5px solid ${nudge.border}`,
      borderRadius: 'var(--radius-lg)',
      padding: '16px 18px',
      marginBottom: 24,
      display: 'flex',
      alignItems: 'flex-start',
      gap: 12,
    }} className="animate-in">
      <span style={{ fontSize: 22, flexShrink: 0, lineHeight: 1.3 }}>{nudge.emoji}</span>
      <div>
        <div style={{ fontWeight: 700, fontSize: '0.9375rem', color: nudge.color, marginBottom: 4 }}>
          {nudge.title}
        </div>
        <div style={{ fontSize: '0.8125rem', color: nudge.color, opacity: 0.85, lineHeight: 1.6 }}>
          {nudge.body}
        </div>
      </div>
    </div>
  );
};

const avatarStyle = {
  width: 44, height: 44, borderRadius: '50%',
  background: 'var(--color-primary-pale)', color: 'var(--color-primary)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  fontWeight: 700, fontSize: '1.1rem',
};

export default ParentDashboard;
